/**
 * Session-scoped deduped image preload for publisher embed + mapping.
 * One network fetch per URL per page lifetime.
 */

let _playIconsWarmed = false;

type ImageCache = Partial<Record<string, Promise<HTMLImageElement | void>>>;

function _imageCacheBucket(): ImageCache {
  if (typeof window === "undefined") {
    return {};
  }
  const pm = window.__pixmoto || {};
  window.__pixmoto = pm;
  if (!pm.imageCache) {
    pm.imageCache = {};
  }
  return pm.imageCache;
}

/**
 * @param {string} url
 * @returns {Promise<HTMLImageElement|void>}
 */
export function preloadImage(url: unknown): Promise<HTMLImageElement | void> {
  if (!url || typeof url !== "string") {
    return Promise.resolve();
  }
  const cache = _imageCacheBucket();
  if (cache[url]) {
    return cache[url];
  }
  const loading = new Promise<HTMLImageElement | void>(function (resolve) {
    try {
      const img = new Image();
      const done = function () {
        resolve(img);
      };
      img.onload = done;
      img.onerror = done;
      img.src = url;
    } catch (_e) {
      resolve();
    }
  });
  cache[url] = loading;
  return loading;
}

/**
 * @param {string[]} urls
 * @returns {Promise<void>}
 */
export function preloadImages(
  urls: (string | null | undefined)[] | null | undefined,
): Promise<void> {
  if (!urls || !urls.length) {
    return Promise.resolve();
  }
  const seen: Record<string, boolean> = {};
  const list: Promise<HTMLImageElement | void>[] = [];
  for (let i = 0; i < urls.length; i++) {
    const u = urls[i];
    if (u && !seen[u]) {
      seen[u] = true;
      list.push(preloadImage(u));
    }
  }
  return Promise.all(list).then(function () {
    /* resolve with undefined */
  });
}

/**
 * Warm default CDN play + hover icons once per page.
 * @param {string} [sharedServer]
 */
export function warmDefaultPlayIcons(sharedServer?: string): Promise<void> {
  if (_playIconsWarmed || typeof window === "undefined") {
    return Promise.resolve();
  }
  _playIconsWarmed = true;
  const host = sharedServer || "cdn.pixmoto.com";
  const proto =
    typeof location !== "undefined" && location.protocol
      ? location.protocol
      : "https:";
  return preloadImages([
    `${proto}//${host}/play/pixmoto-play-icon.png`,
    `${proto}//${host}/play/pixmoto-play-icon-heart-hover.png`,
  ]);
}
